import { OnboardingCompanyIdToNameMap } from "./OnboardingPortal.constants";

export const sanitizeCompanyList = (value: string, other: string): string => {
    return getCompanyPayloadList(value, other).join(", ").trim();
};

const COMPANY_VALUE_PREFIX = "company::";

export const encodeCompanyOptionValue = (company: string): string => {
    return `${COMPANY_VALUE_PREFIX}${encodeURIComponent(company)}`;
};

export const decodeCompanyOptionValue = (value: string): string => {
    if (!value.startsWith(COMPANY_VALUE_PREFIX)) {
        return value;
    }

    return decodeURIComponent(value.slice(COMPANY_VALUE_PREFIX.length));
};

export const isOtherSelected = (companyList: string): boolean => {
    return getCompanyListArray(companyList).includes("Other");
};

export const getCompanyListArray = (
    companyList?: string | string[] | null
): string[] => {
    if (Array.isArray(companyList)) {
        return companyList
            .map((item) => item.trim())
            .filter((item) => item.length > 0);
    }

    if (typeof companyList !== "string") {
        return [];
    }

    return companyList
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
};

export const getCompanyPayloadList = (
    selectedCompanies: string,
    otherCompanies: string
): string[] => {
    const selected = getCompanyListArray(selectedCompanies)
        .map((item) => decodeCompanyOptionValue(item))
        .filter((item) => item !== "Other" && item.length > 0);

    const other = isOtherSelected(selectedCompanies)
        ? getCompanyListArray(otherCompanies).filter(
              (item) => item !== "Other" && item.length > 0
          )
        : [];

    return [...selected, ...other];
};

export const getCompanyListString = (companyArray: string[]): string => {
    return companyArray.join(", ");
};

export const getCompanySelectionForForm = (
    companyList: string | string[] | undefined | null,
    validCompanies: string[]
): {
    selectedCompanies: string;
    otherCompanies: string;
} => {
    const normalizedCompanies = getCompanyListArray(companyList);
    const validCompaniesSet = new Set(validCompanies);
    const selectedCompanies: string[] = [];
    const otherCompanies: string[] = [];

    normalizedCompanies.forEach((company) => {
        if (company === "Other") {
            return;
        }

        if (validCompaniesSet.has(company)) {
            const encodedCompany = encodeCompanyOptionValue(company);
            if (!selectedCompanies.includes(encodedCompany)) {
                selectedCompanies.push(encodedCompany);
            }
            return;
        }

        if (!otherCompanies.includes(company)) {
            otherCompanies.push(company);
        }
    });

    if (
        normalizedCompanies.includes("Other") ||
        otherCompanies.length > 0
    ) {
        selectedCompanies.push("Other");
    }

    return {
        selectedCompanies: selectedCompanies.join(", "),
        otherCompanies: otherCompanies.join(", "),
    };
};

export const getAppCompanySelectionForForm = (
    companyList: string | string[] | undefined | null
): {
    selectedCompanies: string;
    otherCompanies: string;
} => {
    const normalizedCompanies = getCompanyListArray(companyList);
    const selectedCompanies: string[] = [];
    const otherCompanies: string[] = [];

    normalizedCompanies.forEach((company) => {
        if (company === "Other") {
            return;
        }

        const resolvedId = getCompanyIdFromName(company);
        if (resolvedId) {
            const encodedCompany = encodeCompanyOptionValue(resolvedId);
            if (!selectedCompanies.includes(encodedCompany)) {
                selectedCompanies.push(encodedCompany);
            }
            return;
        }

        if (!otherCompanies.includes(company)) {
            otherCompanies.push(company);
        }
    });

    if (
        normalizedCompanies.includes("Other") ||
        otherCompanies.length > 0
    ) {
        selectedCompanies.push("Other");
    }

    return {
        selectedCompanies: selectedCompanies.join(", "),
        otherCompanies: otherCompanies.join(", "),
    };
};

export const generateAppIdentifiers = (
    title: string
): { appId: string; appAlias: string } => {
    // Remove all symbols, keep only letters, numbers, and spaces
    const cleaned = title.replace(/[^a-zA-Z0-9\s]/g, "");

    // Replace all spaces with underscores
    const withUnderscores = cleaned.replace(/\s+/g, "_");

    // Generate 3 random characters (letters and numbers)
    const chars =
        // "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        "0123456789";
    let randomSuffix = "";
    for (let i = 0; i < 4; i++) {
        randomSuffix += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    const result = `${withUnderscores}_${randomSuffix}`;

    return {
        appId: result,
        appAlias: result,
    };
};

const COMPANY_NAME_TO_ID_MAP: Record<string, string> = Object.entries(
    OnboardingCompanyIdToNameMap
).reduce<Record<string, string>>((acc, [id, name]) => {
    acc[name] = id;
    return acc;
}, {});

export const getCompanyIdFromName = (name: string): string | null => {
    if (
        Object.prototype.hasOwnProperty.call(OnboardingCompanyIdToNameMap, name)
    ) {
        return name;
    }

    return COMPANY_NAME_TO_ID_MAP[name] ?? null;
};

export const getCompanyNameFromId = (id: string): string => {
    if (Object.prototype.hasOwnProperty.call(OnboardingCompanyIdToNameMap, id)) {
        return OnboardingCompanyIdToNameMap[
            id as keyof typeof OnboardingCompanyIdToNameMap
        ];
    }

    return id;
};

export const getCompanyDisplayList = (
    companyList: string[] | undefined | null
): string[] => {
    return getCompanyListArray(companyList).map((company) =>
        getCompanyNameFromId(company)
    );
};

export const getCompanyCanonicalKey = (
    value: string | undefined | null
): string => {
    const trimmed = (value || "").trim();
    if (!trimmed) {
        return "";
    }

    const resolvedId = getCompanyIdFromName(trimmed);
    return (resolvedId ?? trimmed).toLowerCase();
};

export const getCompanyCanonicalId = (
    value: string | undefined | null
): string => {
    const trimmed = (value || "").trim();
    if (!trimmed) {
        return "";
    }

    return getCompanyIdFromName(trimmed) ?? trimmed;
};
